import { useNavigate } from 'react-router-dom';
import { Download, Plus, Layers } from 'lucide-react';
import { AuthButton } from '@/core/permissions/AuthButton';
import { DataFilterToolbar, type FilterDefinition } from '@/components/ui/DataFilterToolbar';
import type { EquipmentFilters } from '../types/equipmentList.types';

interface EquipmentToolbarProps {
  filters: EquipmentFilters;
  onSearchChange: (search: string) => void;
  onStatusChange: (status: string) => void;
  onFilterChange?: (key: string, value: string) => void;
  filterOptions?: {
    groups?: { label: string; value: string }[];
  };
}

export function EquipmentToolbar({
  filters,
  onSearchChange,
  onStatusChange,
  onFilterChange,
  filterOptions = { groups: [] },
}: EquipmentToolbarProps) {
  const navigate = useNavigate();

  const filterDefs: FilterDefinition[] = [
    {
      id: 'groupId',
      label: 'Tất cả nhóm thiết bị',
      options: filterOptions.groups || [],
    },
    {
      id: 'status',
      label: 'Tất cả trạng thái',
      options: [
        { label: 'Hoạt động', value: 'active' },
        { label: 'Không hoạt động', value: 'inactive' },
        { label: 'Sẵn sàng', value: 'available' },
        { label: 'Đang sử dụng', value: 'in_use' },
        { label: 'Đã điều phối', value: 'assigned' },
        { label: 'Bảo trì', value: 'maintenance' },
        { label: 'Đang sửa', value: 'repairing' },
        { label: 'Hư hỏng', value: 'damaged' },
        { label: 'Hỏng', value: 'broken' },
        { label: 'Thanh lý', value: 'retired' },
      ]
    },
    {
      id: 'condition',
      label: 'Tất cả tình trạng',
      options: [
        { label: 'Mới/Xuất sắc', value: 'excellent' },
        { label: 'Tốt', value: 'good' },
        { label: 'Khá', value: 'fair' },
        { label: 'Kém', value: 'poor' },
      ]
    }
  ];

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'status') {
      onStatusChange(value || 'all');
    } else if (onFilterChange) {
      onFilterChange(key, value);
    }
  };

  const actions = (
    <>
      <button
        onClick={() => navigate('/equipment/categories')}
        className="h-9 sm:text-sm text-xs border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shrink-0 rounded-md px-3 flex items-center transition-colors font-medium"
      >
        <Layers className="w-4 h-4 mr-2" />
        Nhóm thiết bị
      </button>
      <AuthButton
        module="equipment"
        action="create"
        behavior="hide"
        variant="outline"
        className="h-9 sm:text-sm text-xs border-slate-200 shrink-0"
      >
        <Download className="w-4 h-4 mr-2" />
        Nhập thiết bị
      </AuthButton>
      <AuthButton
        module="equipment"
        action="create"
        behavior="disable"
        permissionReason="Bạn không có quyền thêm thiết bị mới"
        className="bg-emerald-600 hover:bg-emerald-700 text-white border-none h-9 sm:text-sm text-xs shrink-0 rounded-md px-3 flex items-center"
        onClick={() => navigate('/equipment/new')}
      >
        <Plus className="w-4 h-4 mr-2" />
        Thêm thiết bị
      </AuthButton>
    </>
  );

  return (
    <DataFilterToolbar
      search={filters.search || ''}
      onSearchChange={onSearchChange}
      searchPlaceholder="Tìm kiếm theo tên, mã thiết bị..."
      filterDefs={filterDefs}
      filterValues={{ ...filters, status: filters.status === 'all' ? '' : filters.status }}
      onFilterChange={handleFilterChange}
      actions={actions}
    />
  );
}
